/**
 * Altara Trust Verification · slide asset
 *
 * Show-and-tell of the Trust stage of My Decisions. Three verification
 * checks flip green one after the other, evidence rows slide in with
 * their real values, and the Trust chip at the top lights up in gold
 * once all three pass — the moment Nomsa can safely advance.
 *
 * Embed mode: /wallet/demos/altara-trust-verify?embed=1&autoplay=1
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Check, Loader2, RotateCcw, Building2, User,
  BadgeCheck, FileText, Sparkles, ArrowRight, ExternalLink,
  Landmark, IdCard, Play,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

const STAGES = ["Trust", "Understand", "Check", "Act"];

/* Evidence data — deterministic Nomsa scenario. */
const CHECKS = [
  {
    id: "participating",
    title: "Proposal came from a participating institution",
    Icon: Landmark,
    lookup: null,   // internal Altara check — no external query
    rows: [
      ["Proposal ID",       "doc_6e7c846883 · v1.0"],
      ["Source channel",    "Participating institution · Altara-signed"],
      ["Delivered",         "2026/09/09, 14:02:05"],
    ],
  },
  {
    id: "adviser",
    title: "Adviser identity",
    subtitle: "James Smith · Senior Financial Adviser",
    Icon: IdCard,
    lookup: {
      source: "FSCA · Authorised Representatives register",
      query:  "Rep #A-2214-JS · James Smith",
      match:  "James Smith · Rep #A-2214-JS · attached to FSP #48291",
    },
    rows: [
      ["Full name",     "James Smith"],
      ["FAIS reg no.",  "FAIS Rep #A-2214-JS"],
      ["Role",          "Senior Financial Adviser"],
      ["Attached to",   "ABC Wealth"],
    ],
  },
  {
    id: "institution",
    title: "Institution verified via Altara KY-I",
    Icon: Building2,
    lookup: {
      source: "FSCA · Financial Services Providers register",
      query:  "FSP #48291 · ABC Wealth",
      match:  "ABC Wealth · FSP #48291 · Authorised · active",
    },
    rows: [
      ["Institution",     "ABC Wealth"],
      ["Registration",    "FSCA FSP #48291"],
      ["Verified via",    "Altara Trust Utility · KY-I registry"],
      ["KY-I status",     "Verified · in good standing"],
    ],
  },
];

export default function WalletAltaraTrustVerifyDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";

  if (embed) {
    return (
      <div
        className="w-screen h-screen flex items-center justify-center px-8 py-6 overflow-hidden relative"
        style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
        data-testid="altara-trust-verify-embed"
      >
        <div className="w-full max-w-5xl">
          <EmbedHeader />
          <div className="mt-6">
            <VerifyStage autoplay={autoplay} />
          </div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }

  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-trust-verify">
        <Header />
        <DownloadStrip />
        <VerifyStage />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        Altara verifies the adviser and institution
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        Before Nomsa reads a word of the proposal, Altara checks who the sender
        actually is. Three verification checks flip green — then the Trust icon
        lights up and the decision is safe to advance.
      </p>
    </header>
  );
}

function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence
      </div>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1" style={{ color: NAVY }}>
        Verifying the adviser and institution
      </h1>
      <div className="text-sm text-slate-600 mt-1.5">
        Three checks · one moment of trust.
      </div>
    </div>
  );
}

function SlideBrandMark() {
  return (
    <div className="absolute bottom-4 right-6 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}

function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
          Slide assets
        </div>
        <div className="text-sm text-slate-700 mt-0.5">
          Drop-in animation for a PowerPoint deck — MP4 or animated GIF.
        </div>
      </div>
      <a href="/altara-trust-verify.mp4" download
         data-testid="download-mp4"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
         style={{ background: NAVY }}>Download MP4 <ArrowRight className="w-3.5 h-3.5" /></a>
      <a href="/altara-trust-verify.gif" download
         data-testid="download-gif"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
         style={{ background: "white", borderColor: NAVY, color: NAVY }}>Download GIF <ArrowRight className="w-3.5 h-3.5" /></a>
      <Link to="/wallet/demos/altara-trust-verify?embed=1"
            target="_blank"
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold"
            style={{ background: `${GOLD}22`, color: NAVY }}>Open embed view <ExternalLink className="w-3.5 h-3.5" /></Link>
    </div>
  );
}

function Footnote() {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 flex items-start gap-3">
      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
      <div className="text-[12px] text-slate-600 leading-relaxed">
        <strong className="text-slate-800">How the check works.</strong> Each
        proof is compared against Nomsa&apos;s existing My Trusted Institutions record
        and the Altara KY-I registry. All three lookups are deterministic — the
        Trust stage never asks Nomsa to judge whether an adviser is real.
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Verify stage — the animated card that becomes the slide asset
   ══════════════════════════════════════════════════════════════ */

function VerifyStage({ autoplay = false }) {
  const [passedIdx, setPassedIdx] = useState(-1);      // -1 = none, 0..2 = through that index
  const [activeIdx, setActiveIdx] = useState(-1);      // currently animating check
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const timers = useRef([]);
  const autoplayFired = useRef(false);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const play = () => {
    if (busy) return;
    clearTimers();
    setPassedIdx(-1); setActiveIdx(-1); setComplete(false); setBusy(true);

    // Sequence (a touch longer than v1 to let the FSCA lookup animate):
    //   +200ms  → activate #0 (participating — internal, quick)
    //   +1400ms → pass #0, activate #1 (adviser — FSCA lookup)
    //   +3300ms → pass #1, activate #2 (institution — FSCA lookup)
    //   +5200ms → pass #2 (all done) → Trust chip lights up
    //   +6100ms → mark complete
    const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));
    t(200,  () => setActiveIdx(0));
    t(1400, () => { setPassedIdx(0); setActiveIdx(1); });
    t(3300, () => { setPassedIdx(1); setActiveIdx(2); });
    t(5200, () => { setPassedIdx(2); setActiveIdx(-1); });
    t(6100, () => { setComplete(true); setBusy(false); });
  };

  const reset = () => {
    clearTimers();
    setPassedIdx(-1); setActiveIdx(-1); setComplete(false); setBusy(false);
  };

  useEffect(() => {
    if (!autoplay || autoplayFired.current) return;
    autoplayFired.current = true;
    // Deliberately no cleanup — surviving StrictMode's double-mount lets
    // the recording script observe a single deterministic playback.
    setTimeout(play, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);

  useEffect(() => () => clearTimers(), []);

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
         data-testid="verify-stage">
      {/* Header row */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: `${NAVY}12` }}>
          <Landmark className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
            Investment proposal · v1.0
          </div>
          <div className="font-display text-lg font-semibold leading-tight" style={{ color: NAVY }}>
            ABC Wealth Balanced Growth Portfolio
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Sent by <span className="font-medium text-slate-700">ABC Wealth</span> · via <span className="font-medium text-slate-700">James Smith</span>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-[9px] tracking-widest uppercase text-slate-400">Commitment</div>
          <div className="font-display text-base font-semibold" style={{ color: NAVY }}>R 450 000</div>
          <div className="text-[10px] text-slate-500">over 60 months</div>
        </div>
      </div>

      {/* Stage chips */}
      <div className="px-6 pt-4">
        <StageChips complete={complete} />
      </div>

      {/* Explainer */}
      <div className="px-6 py-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
             style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #F5EFE6 100%)` }}>
          <User className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="text-[13px] text-slate-700 leading-relaxed">
          <strong style={{ color: NAVY }}>Before you spend a minute understanding this proposal…</strong><br />
          First, let&apos;s confirm this proposal is legitimately from a real, verified institution and adviser. If any of these details look wrong, stop here and tell me — I&apos;ll help you check.
        </div>
      </div>

      {/* Check rows */}
      <div className="px-6 pb-4 space-y-3">
        {CHECKS.map((c, i) => (
          <CheckRow
            key={c.id}
            check={c}
            state={
              passedIdx >= i ? "passed" :
              activeIdx === i ? "checking" : "pending"
            }
            index={i}
          />
        ))}
      </div>

      {/* Advance banner */}
      <div className="px-6 pb-6">
        <AnimatePresence>
          {complete && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border p-4 flex items-center gap-3"
              style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }}
              data-testid="advance-banner"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                   style={{ background: "#065F46" }}>
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div className="flex-1 text-[13px]" style={{ color: "#065F46" }}>
                All three checks passed. <strong>Trust</strong> is confirmed — Nomsa can move on to <strong>Understand</strong>.
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!complete && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-[11px] text-slate-500">
              When all three checks pass, this decision advances to <span className="font-semibold text-slate-700">Understand</span>.
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={play}
                disabled={busy}
                data-testid="play-verify"
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                style={{ background: NAVY }}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                {busy ? "Verifying…" : "Play verification"}
              </button>
              <button
                onClick={reset}
                data-testid="reset-verify"
                className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-[12px] font-medium border"
                style={{ background: "white", borderColor: "#E2E8F0", color: NAVY }}
                title="Reset"
              ><RotateCcw className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Stage chip row (Trust · Understand · Check · Act) */
function StageChips({ complete }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="stage-chips">
      {STAGES.map((s, i) => {
        const isTrust = i === 0;
        const trustLit = isTrust && complete;
        return (
          <motion.div
            key={s}
            data-testid={`stage-chip-${s.toLowerCase()}${trustLit ? "-lit" : ""}`}
            animate={trustLit ? {
              boxShadow: [
                `0 0 0 0 ${GOLD}00`,
                `0 0 0 10px ${GOLD}00`,
              ],
              scale: [1, 1.06, 1],
            } : {}}
            transition={trustLit ? { duration: 1.2, repeat: 1 } : {}}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border"
            style={{
              background: isTrust ? NAVY : "white",
              color: isTrust ? "white" : "#94a3b8",
              borderColor: isTrust ? NAVY : "#E2E8F0",
              boxShadow: trustLit ? `0 0 0 3px ${GOLD}, 0 0 24px ${GOLD}` : "none",
            }}
          >
            {trustLit ? (
              <BadgeCheck className="w-3.5 h-3.5" style={{ color: GOLD }} />
            ) : isTrust ? (
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: GOLD }} />
            ) : (
              <div className="w-3.5 h-3.5 rounded-full border border-slate-300" />
            )}
            {s}
          </motion.div>
        );
      })}
    </div>
  );
}

/* Individual verification row */
function CheckRow({ check, state, index }) {
  const { title, subtitle, Icon, rows, lookup } = check;
  return (
    <motion.div
      layout
      initial={false}
      animate={{
        borderColor: state === "passed" ? "#6EE7B7" :
                     state === "checking" ? GOLD : "#E2E8F0",
        boxShadow: state === "checking" ? `0 0 0 3px ${GOLD}22` : "none",
      }}
      transition={{ duration: 0.35 }}
      className="rounded-xl border bg-white p-4"
      data-testid={`check-row-${check.id}-${state}`}
    >
      <div className="flex items-center gap-3">
        <StateBadge state={state} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <IconStamp state={state} Icon={Icon} />
      </div>

      {/* FSCA lookup panel — only while this row is checking, and only for
          rows that have a lookup source. */}
      <AnimatePresence>
        {state === "checking" && lookup && (
          <FscaLookupPanel lookup={lookup} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {state === "passed" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-3 pt-3 border-t border-slate-100"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
              {rows.map(([label, value], j) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + j * 0.08, duration: 0.25 }}
                  className="flex items-baseline justify-between gap-3 text-[11.5px]"
                >
                  <span className="tracking-[0.18em] uppercase text-slate-400 text-[10px] flex-shrink-0">{label}</span>
                  <span className="font-medium text-slate-700 text-right truncate">{value}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/** FSCA registry lookup — a tiny "we're actually querying the regulator"
 *  animation. Slides in with the query, then flips to a MATCH row. */
function FscaLookupPanel({ lookup }) {
  const [matched, setMatched] = useState(false);
  useEffect(() => {
    setMatched(false);
    const t = setTimeout(() => setMatched(true), 950);
    return () => clearTimeout(t);
  }, [lookup]);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-3 pt-3 border-t border-slate-100 overflow-hidden"
      data-testid="fsca-lookup-panel"
    >
      <div className="rounded-lg border p-2.5 flex items-center gap-3"
           style={{ background: "#FFF8EF", borderColor: "#F5C481" }}>
        {/* FSCA-style orange building tile — mimics the reference screenshot */}
        <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
             style={{ background: "#EA8C1B" }}>
          <Landmark className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9.5px] tracking-[0.24em] uppercase font-semibold" style={{ color: "#B75E00" }}>
            {lookup.source}
          </div>
          <AnimatePresence mode="wait">
            {!matched ? (
              <motion.div
                key="querying"
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="text-[12px] font-medium flex items-center gap-2 mt-0.5"
                style={{ color: "#7C3A00" }}
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Querying: <span className="font-semibold">{lookup.query}</span>
              </motion.div>
            ) : (
              <motion.div
                key="match"
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
                className="text-[12px] font-medium flex items-center gap-2 mt-0.5"
                style={{ color: "#065F46" }}
              >
                <BadgeCheck className="w-3.5 h-3.5" />
                <span className="uppercase tracking-widest text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: "#ECFDF5", color: "#065F46" }}>Match</span>
                <span className="text-slate-700">{lookup.match}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

function StateBadge({ state }) {
  if (state === "passed") {
    return (
      <motion.div
        initial={{ scale: 0.6 }} animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 20 }}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "#10B981" }}
      >
        <Check className="w-4 h-4 text-white" strokeWidth={3} />
      </motion.div>
    );
  }
  if (state === "checking") {
    return (
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
           style={{ background: `${GOLD}22`, border: `1.5px solid ${GOLD}` }}>
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-slate-200 bg-white" />
  );
}

function IconStamp({ state, Icon }) {
  return (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
         style={{
           background: state === "passed" ? "#ECFDF5" : `${NAVY}0A`,
           color: state === "passed" ? "#065F46" : NAVY,
         }}>
      <Icon className="w-4 h-4" />
    </div>
  );
}
